import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';

export default function VenderImovelSergipePage() {
  // SEO
  useSEO({
    title: 'Vender Imóvel em Sergipe - Aracaju e Barra dos Coqueiros | Mateus Oliveira',
    description: 'Venda seu imóvel em Sergipe com rapidez e segurança. Avaliação gratuita, divulgação profissional e atendimento especializado em Aracaju e Barra dos Coqueiros. WhatsApp (79) 99988-1002',
    keywords: 'vender imóvel sergipe, vender casa aracaju, vender apartamento sergipe, avaliação imóvel aracaju, corretor sergipe, vender imóvel barra dos coqueiros',
    canonical: window.location.origin + '/vender-imovel-sergipe',
    schema: [
      generateServiceSchema(
        'Venda de Imóveis em Sergipe',
        'Serviço completo de venda de imóveis em Aracaju e Barra dos Coqueiros com avaliação gratuita, divulgação profissional e acompanhamento jurídico.'
      ),
      generateBreadcrumbSchema([
        { name: 'Vender Imóvel em Sergipe', url: '/vender-imovel-sergipe' },
      ]),
    ],
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { name: 'Vender Imóvel em Sergipe', url: '/vender-imovel-sergipe' },
            ]}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Vender Imóvel em <span className="text-orange-500">Sergipe</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Venda seu imóvel com rapidez, segurança e pelo melhor preço. 
              Atendimento especializado em Aracaju e Barra dos Coqueiros.
            </p>
          </motion.div>

          {/* Benefícios */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-speed-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Venda Rápida</h3>
              <p className="text-gray-600">
                Divulgação profissional em múltiplos canais para vender mais rápido
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Melhor Preço</h3>
              <p className="text-gray-600">
                Avaliação profissional para conseguir o melhor valor de mercado
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-3xl text-green-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Segurança Total</h3>
              <p className="text-gray-600">
                Acompanhamento jurídico e documental em todo o processo
              </p>
            </motion.div>
          </div>

          {/* Processo de Venda */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-lg mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Como Funciona o Processo de Venda
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Avaliação Gratuita</h3>
                <p className="text-gray-600 text-sm">
                  Avaliamos seu imóvel gratuitamente e definimos o melhor preço
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-orange-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Fotos e Vídeos</h3>
                <p className="text-gray-600 text-sm">
                  Produzimos material profissional para destacar seu imóvel
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-orange-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Divulgação</h3>
                <p className="text-gray-600 text-sm">
                  Anunciamos em portais, redes sociais e nosso site
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-orange-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Fechamento</h3>
                <p className="text-gray-600 text-sm">
                  Acompanhamos até a assinatura do contrato e recebimento
                </p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-12 text-center text-white shadow-2xl mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pronto para Vender Seu Imóvel?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Solicite uma avaliação gratuita agora mesmo!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/avaliar-imovel"
                className="inline-block bg-white text-orange-500 hover:bg-gray-100 font-bold py-4 px-10 rounded-full text-lg transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calculator-line mr-2"></i>
                Avaliar Meu Imóvel
              </Link>
              <a
                href="https://wa.me/5579999881002"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-10 rounded-full text-lg transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Conteúdo SEO */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Por Que Vender Seu Imóvel com a Mateus Oliveira?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p>
                <strong>Vender um imóvel em Sergipe</strong> requer conhecimento do mercado local, 
                estratégias de divulgação eficientes e acompanhamento profissional. A 
                <strong> Mateus Oliveira Imóveis</strong> oferece tudo isso e muito mais.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Diferenciais do Nosso Serviço
              </h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Avaliação precisa:</strong> Análise detalhada do mercado para definir o melhor preço</li>
                <li><strong>Marketing profissional:</strong> Fotos, vídeos e tours virtuais de alta qualidade</li>
                <li><strong>Divulgação ampla:</strong> Anúncios em múltiplos portais e redes sociais</li>
                <li><strong>Qualificação de leads:</strong> Filtramos compradores realmente interessados</li>
                <li><strong>Negociação especializada:</strong> Conseguimos as melhores condições para você</li>
                <li><strong>Suporte jurídico:</strong> Acompanhamento de toda a documentação</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Mercado Imobiliário em Sergipe
              </h3>
              <p>
                O mercado imobiliário de <strong>Aracaju</strong> e <strong>Barra dos Coqueiros</strong> está 
                em constante crescimento, com alta demanda por imóveis de qualidade. Nossa expertise local 
                garante que seu imóvel seja vendido pelo melhor preço e no menor tempo possível.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-6 mb-4">
                Documentação Necessária
              </h3>
              <p>
                Para vender seu imóvel, você precisará de:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Escritura ou contrato de compra e venda</li>
                <li>IPTU atualizado</li>
                <li>Certidões negativas (débitos, ônus reais)</li>
                <li>Matrícula atualizada do imóvel</li>
                <li>Documentos pessoais (RG, CPF, comprovante de residência)</li>
              </ul>
              <p className="mt-4">
                Não se preocupe! Nossa equipe ajuda você a reunir toda a documentação necessária.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mt-8">
                <h4 className="text-xl font-bold text-gray-800 mb-2">
                  Comece Agora!
                </h4>
                <p className="mb-4">
                  Entre em contato e receba uma avaliação gratuita do seu imóvel. 
                  Vamos vender juntos pelo melhor preço!
                </p>
                <p className="font-bold text-lg">
                  <i className="ri-whatsapp-line text-green-500 mr-2"></i>
                  WhatsApp: (79) 99988-1002
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
